import { type BoxProps } from '@chakra-ui/react';
import { TokenSelector } from '@ui/components/Input/TokenSelector';
import { FromTokenCard } from '@ui/components/SwapDialog/FromTokenCard';
import { Token } from '@ui/config/token';
import { useTokenBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useTokenConfig } from '@ui/hooks/contracts/useTokenConfig';
import { useTokenPrice } from '@ui/hooks/tokenPrice';
import { useAmountInputError } from '@ui/hooks/useAmountInputError';
import { useMaxValue } from '@ui/hooks/useMaxValue';
import { validateNotEmpty } from '@ui/utils/validations';
import { useTranslations } from 'next-intl';

import { useBondingTradeDialog } from '../../useBondingTradeDialog';
import { BondingNormalTokenSelector } from './BondingNormalTokenSelector';
import { useBondingTradeForm } from './useBondingTradeForm';

export const BondingTradeFromTokenCard = (props: BoxProps) => {
  const t = useTranslations();
  const enteredAmountStr = useBondingTradeForm((s) => s.enteredAmountStr);
  const enteredAmount = useBondingTradeForm((s) => s.enteredAmount);
  const fromToken = useBondingTradeForm((s) => s.fromToken);
  const bondingTokenAddress = useBondingTradeDialog(
    (s) => s.bondingTokenAddress,
  );
  const fromTokenInfo = useTokenConfig({
    token: fromToken,
    tokenAddress: bondingTokenAddress,
  });

  const inputError = useAmountInputError({
    fromToken: fromToken,
    tokenAddress: bondingTokenAddress,
    enteredAmountStr: enteredAmountStr,
  });

  const usdPrice = useTokenPrice({
    token:
      fromToken === Token.Bonding
        ? { type: Token.Bonding, bondingAddress: bondingTokenAddress }
        : fromToken,
  }).data?.usd_price;
  const balance = useTokenBalance({
    tokenAddress: fromTokenInfo?.address,
  }).data;
  const maxValue = useMaxValue(fromToken, balance);

  const tokenSelector =
    fromToken === Token.Bonding ? (
      <TokenSelector token={fromToken} tokenAddress={bondingTokenAddress} />
    ) : (
      <BondingNormalTokenSelector isSelectable={false} />
    );

  return (
    <FromTokenCard
      enteredAmountStr={enteredAmountStr}
      enteredAmount={enteredAmount}
      fromTokenInfo={fromTokenInfo}
      usdPrice={usdPrice}
      balance={balance}
      maxValue={maxValue}
      inputError={
        validateNotEmpty({
          t,
          value: enteredAmountStr,
        })
          ? null
          : inputError
      }
      onAmountChange={(value) =>
        useBondingTradeForm.getState().setEnteredAmountStr(value)
      }
      tokenSelector={tokenSelector}
      {...props}
    />
  );
};
